import React, { useMemo, useState } from 'react';
import { api } from '../api';
import { parseLines, fmtDateTimeLocal, fmtDate } from '../lib/ui';
import { EmptyState } from '../lib/illustrations';
import ManualBar from '../components/ManualBar';
import {
  IconRocket,
  IconCalendar,
  IconPen,
  IconCheck,
  IconExternal,
  IconInfo,
  IconPlug,
  PlatformGlyph,
} from '../lib/icons';

export default function Composer({ platforms, accounts, onRefresh, navigate }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [media, setMedia] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const [rotate, setRotate] = useState(true);
  const [selected, setSelected] = useState([]);
  const [scheduleAt, setScheduleAt] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const mediaUrls = parseLines(media);

  const accountOptions = useMemo(() => {
    const byPlatform = {};
    (accounts || []).forEach((acc) => {
      if (!byPlatform[acc.platform]) byPlatform[acc.platform] = [];
      byPlatform[acc.platform].push(acc);
    });
    const out = [];
    (platforms || []).forEach((p) => {
      const own = byPlatform[p.id] || [];
      if (own.length === 0) {
        out.push({
          key: p.id,
          platform: p.id,
          accountId: null,
          label: `${p.name} · sem conta`,
          profile: '',
          color: p.color,
        });
        return;
      }
      own.forEach((acc) => {
        out.push({
          key: `${p.id}:${acc.id}`,
          platform: p.id,
          accountId: acc.id,
          label: `${p.name} · ${acc.label || acc.username || acc.id}`,
          profile: acc.profile || '',
          color: p.color,
        });
      });
    });
    return out;
  }, [platforms, accounts]);

  const toggleTarget = (key) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]));
  };

  const selectAll = () => setSelected(accountOptions.map((o) => o.key));
  const clearAll = () => setSelected([]);

  const picked = accountOptions.filter((o) => selected.includes(o.key));

  const buildPayload = () => ({
    title: title.trim(),
    content: content.trim(),
    mediaUrls,
    mediaType: mediaUrls.length ? mediaType : 'none',
    rotateMedia: rotate,
    targets: [...new Set(picked.map((o) => o.platform))],
    accountTargets: picked
      .filter((o) => o.accountId)
      .map((o) => ({ platform: o.platform, accountId: o.accountId })),
  });

  const noTargets = 'Selecione ao menos uma conta ou plataforma.';

  const publishNow = async () => {
    setError('');
    setResult(null);
    if (selected.length === 0) return setError(noTargets);
    setBusy('publish');
    try {
      const pub = await api.post('/publications', buildPayload());
      const done = await api.post(`/publications/${pub.id}/publish`, {});
      setResult(done);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const schedule = async () => {
    setError('');
    setResult(null);
    if (selected.length === 0) return setError(noTargets);
    if (!scheduleAt) return setError('Escolha a data e a hora do agendamento.');
    setBusy('schedule');
    try {
      const pub = await api.post('/publications', { ...buildPayload(), scheduleAt });
      setResult(pub);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const now = fmtDateTimeLocal(new Date().toISOString()).slice(0, 16);

  return (
    <div className="composer">
      <section className="section">
        <div className="field">
          <label>Título</label>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex.: Promoção de fim de semana"
          />
        </div>

        <div className="field">
          <label>Conteúdo / legenda</label>
          <textarea
            className="textarea"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Escreva a mensagem que será publicada em todas as plataformas…"
          />
        </div>

        <div className="field">
          <label>Mídia (um URL por linha)</label>
          <textarea
            className="textarea"
            style={{ minHeight: 80 }}
            value={media}
            onChange={(e) => setMedia(e.target.value)}
            placeholder={'https://exemplo.com/foto1.jpg\nhttps://exemplo.com/video.mp4'}
          />
        </div>

        <div className="field" style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>Tipo de mídia</label>
            <select className="select" value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
              <option value="image">Imagem</option>
              <option value="video">Vídeo</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 11 }}>
            <label className="switch-row">
              <input type="checkbox" checked={rotate} onChange={(e) => setRotate(e.target.checked)} />
              Rotacionar imagens entre publicações
            </label>
          </div>
        </div>

        <div className="field">
          <label>
            Contas ({selected.length} selecionadas)
            <span style={{ float: 'right', fontWeight: 400 }}>
              <button type="button" className="btn ghost small" onClick={selectAll}>
                Selecionar todas
              </button>{' '}
              <button type="button" className="btn ghost small" onClick={clearAll}>
                Limpar
              </button>
            </span>
          </label>
          {accountOptions.length === 0 ? (
            <EmptyState kind="users" size={130} title="Nenhuma conta conectada.">
              <button type="button" className="btn primary small" onClick={() => navigate('accounts')}>
                <span className="btn-ico">
                  <IconPlug size={14} />
                </span>
                Conectar contas
              </button>
            </EmptyState>
          ) : null}
          <div className="chips">
            {accountOptions.map((o) => (
              <button
                type="button"
                key={o.key}
                className={`chip ${selected.includes(o.key) ? 'on' : ''}`}
                title={o.profile ? `Perfil do navegador: ${o.profile}` : o.label}
                onClick={() => toggleTarget(o.key)}
              >
                <span className="chip-dot" style={{ background: o.color }} />
                {o.label}
                {o.profile ? ` · ${o.profile}` : ''}
              </button>
            ))}
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            Cada chip é uma conta. Use um perfil do navegador por conta para manter todas logadas ao mesmo tempo.
          </div>
        </div>

        {error ? <div className="banner err">{error}</div> : null}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn primary" onClick={publishNow} disabled={busy !== ''}>
            <span className="btn-ico">
              <IconRocket size={15} />
            </span>
            {busy === 'publish' ? 'Publicando…' : 'Publicar agora nas selecionadas'}
          </button>
          <input
            className="input"
            type="datetime-local"
            style={{ maxWidth: 230 }}
            value={scheduleAt}
            min={now}
            onChange={(e) => setScheduleAt(e.target.value)}
          />
          <button type="button" className="btn success" onClick={schedule} disabled={busy !== ''}>
            <span className="btn-ico">
              <IconCalendar size={15} />
            </span>
            {busy === 'schedule' ? 'Agendando…' : 'Agendar'}
          </button>
        </div>
      </section>

      <div>
        <section className="section">
          <div className="section-head">
            <h2>
              <span className="head-ico">
                <IconPen size={16} />
              </span>
              Pré-visualização
            </h2>
          </div>
          <div className="preview">
            <div className="preview-title">{title || '(sem título)'}</div>
            {content || '(sem conteúdo)'}
          </div>
          <div className="hint" style={{ marginTop: 10 }}>
            {mediaUrls.length > 0
              ? `${mediaUrls.length} mídia(s) anexada(s)${rotate && mediaUrls.length > 1 ? ' · rotação ativa' : ''}`
              : 'Nenhuma mídia anexada'}
          </div>
        </section>

        {result ? (
          <ResultPanel result={result} accounts={accounts} platforms={platforms} navigate={navigate} onRefresh={onRefresh} />
        ) : null}

        <section className="section">
          <div className="section-head">
            <h2>
              <span className="head-ico">
                <IconInfo size={16} />
              </span>
              Dica
            </h2>
          </div>
          <div className="hint">
            Clique em <strong>“Selecionar todas”</strong> e depois em <strong>“Publicar agora”</strong> para enviar a
            mesma publicação para todas as contas conectadas em um único clique. Depois é só confirmar conta por conta.
          </div>
        </section>
      </div>
    </div>
  );
}

function ResultPanel({ result, accounts, platforms, navigate, onRefresh }) {
  const [data, setData] = useState(result);
  const [busy, setBusy] = useState('');
  const results = data.results || {};
  const keys = Object.keys(results);
  const scheduled = data.status === 'scheduled';
  const manual = data.status === 'manual' || keys.some((k) => results[k] && results[k].manual);

  const accountMap = {};
  (accounts || []).forEach((acc) => {
    accountMap[acc.id] = acc;
  });

  const colorOf = (pid) => {
    const p = (platforms || []).find((x) => x.id === pid);
    return p ? p.color : '#6d5cff';
  };

  const rows = [];
  keys.forEach((pid) => {
    const r = results[pid];
    if (!r) return;
    const accountsRes = r.accounts && Object.keys(r.accounts).length ? r.accounts : null;
    if (accountsRes) {
      Object.keys(accountsRes).forEach((aid) => {
        rows.push({ key: `${pid}:${aid}`, platform: pid, accountId: aid, entry: accountsRes[aid], platformOk: r.ok });
      });
    } else {
      rows.push({ key: pid, platform: pid, accountId: null, entry: r, platformOk: r.ok });
    }
  });

  const confirm = async (pid, accountId) => {
    const current = results[pid] || {};
    const entry = accountId ? (current.accounts || {})[accountId] : current;
    const prefill = entry && entry.ok ? entry.url || '' : '';
    const url = window.prompt('Link do post publicado (opcional):', prefill);
    if (url === null) return;
    setBusy(accountId ? `${pid}:${accountId}` : pid);
    try {
      const updated = await api.post(`/publications/${data.id}/confirm`, {
        platform: pid,
        accountId: accountId || undefined,
        url: url || '',
      });
      setData(updated);
      onRefresh();
    } catch (err) {
      // erro simples sem interromper
    } finally {
      setBusy('');
    }
  };

  const openSite = (url) => {
    if (url) window.open(url, '_blank', 'noopener');
  };

  const refreshMetrics = async () => {
    try {
      await api.post(`/publications/${data.id}/refresh-metrics`, {});
      onRefresh();
      navigate('analytics');
    } catch (err) {
      // ignora
    }
  };

  const statusCls =
    data.status === 'published' ? 'ok' : data.status === 'partial' || data.status === 'manual' || scheduled ? 'warn' : 'err';

  return (
    <section className="section">
      <div className="section-head">
        <h2>
          <span className="head-ico">
            {scheduled ? <IconCalendar size={16} /> : manual ? <IconExternal size={16} /> : <IconRocket size={16} />}
          </span>
          {scheduled ? 'Agendada com sucesso' : manual ? 'Publicação manual assistida' : 'Resultado da publicação'}
        </h2>
        <span className={`badge ${statusCls}`}>{data.status}</span>
      </div>

      {scheduled ? (
        <div className="hint">
          Programada para {fmtDate(data.scheduleAt)}. O agendador executa automaticamente quando chegar a hora.
        </div>
      ) : null}

      {manual ? (
        <div className="banner info">
          Para cada conta abaixo, clique em <strong>Abrir site</strong>, publique com a conta logada e depois clique em{' '}
          <strong>Confirmar</strong> (cole o link do post, se quiser).
        </div>
      ) : null}

      {manual ? (
        <ManualBar rows={rows} colorOf={colorOf} onConfirm={confirm} busy={busy} />
      ) : null}

      {rows.length > 0 ? (
        <div className="list" style={{ marginTop: 10 }}>
          {rows.map(({ key, platform, accountId, entry }) => {
            const acct = accountId ? accountMap[accountId] : null;
            const pendingManual = entry && entry.manual && entry.pending && !entry.ok;
            const busyKey = accountId ? `${platform}:${accountId}` : platform;
            const name = acct ? acct.label || acct.username || accountId : platform;
            return (
              <div className="row" key={key}>
                <div style={{ minWidth: 0 }}>
                  <div className="title">
                    <span className="row-logo" style={{ background: colorOf(platform) }}>
                      <PlatformGlyph id={platform} size={14} />
                    </span>
                    {name}
                    {acct && acct.profile ? <span className="meta"> · {acct.profile}</span> : null}
                  </div>
                  <div className={entry.ok ? 'result-ok' : 'result-err'}>
                    {entry.ok
                      ? entry.message || 'Publicado'
                      : pendingManual
                        ? 'Aguardando publicação manual'
                        : entry.error}
                  </div>
                  {entry.url ? (
                    <div className="meta">
                      <a className="mono" href={entry.url} target="_blank" rel="noopener noreferrer">
                        {entry.url}
                      </a>
                      {entry.demo ? ' (demo)' : ''}
                    </div>
                  ) : null}
                  {acct && acct.profile && pendingManual ? (
                    <div className="hint">Use o perfil “{acct.profile}” no navegador antes de abrir o site.</div>
                  ) : null}
                </div>
                {pendingManual ? (
                  <div className="actions">
                    <button type="button" className="btn primary small" onClick={() => openSite(entry.url)}>
                      <span className="btn-ico">
                        <IconExternal size={14} />
                      </span>
                      Abrir site
                    </button>
                    <button
                      type="button"
                      className="btn success small"
                      disabled={busy !== ''}
                      onClick={() => confirm(platform, accountId)}
                    >
                      <span className="btn-ico">
                        <IconCheck size={14} />
                      </span>
                      {busy === busyKey ? '…' : 'Confirmar'}
                    </button>
                  </div>
                ) : (
                  <span className={`badge ${entry.ok ? 'ok' : 'err'}`}>{entry.ok ? 'OK' : 'Falhou'}</span>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 9, marginTop: 14 }}>
        <button type="button" className="btn ghost small" onClick={() => navigate('publications')}>
          Ver publicações
        </button>
        {!scheduled && !manual && keys.length > 0 ? (
          <button type="button" className="btn success small" onClick={refreshMetrics}>
            Coletar métricas
          </button>
        ) : null}
      </div>
    </section>
  );
}
